import { ReactNode } from "react";
import { Button, Divider, Menu, MenuItem, SxProps, Theme } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// Shared manage-page row-action menu (platform UI/UX polish, Phase A).
// NO Phase A consumer — ManageMembers keeps its two-button Actions cell.
// Defaults mirror ManageEvents' existing page-level menu (the declarative
// actionsByStatus matrix pattern); ManageInvoices / ManageEvents /
// ManageEmails adopt in Phase B. Usage pattern: a SINGLE page-level instance
// anchored via state { row, anchorEl } — NOT one Menu per row.

export type RowActionDescriptor<ActionKey extends string = string> = {
  key: ActionKey;
  label: string;
  // rendered before the label (Events wraps icons in a ListItemIcon with
  // minWidth 32; Emails uses className "mr-3")
  icon?: ReactNode;
  // red item styling
  destructive?: boolean;
  // a <Divider/> is emitted immediately before this item (suppressed when
  // the item ends up first after hidden items are filtered out)
  dividerBefore?: boolean;
  disabled?: boolean;
};

// Exactly ManageEvents' actionsByStatus shape.
export type RowActionMatrix<Status extends string, ActionKey extends string> =
  Readonly<Record<Status, ReadonlyArray<ActionKey>>>;

export type BuildRowActionsConfig<
  Status extends string,
  ActionKey extends string,
  Row,
> = {
  matrix: RowActionMatrix<Status, ActionKey>;
  labels: Readonly<Record<ActionKey, string>>;
  // Events: deriveStatus
  getStatus: (row: Row) => Status;
  // Events: dangerActions
  destructiveActions?: ReadonlySet<ActionKey>;
  // Events: new Set(["delete"])
  dividerBeforeActions?: ReadonlySet<ActionKey>;
  getIcon?: (action: ActionKey, row: Row) => ReactNode;
  // Invoices: actionLoading / !canUseAction gates
  isDisabled?: (action: ActionKey, row: Row) => boolean;
  // Emails: viewAnalytics hidden for drafts; Invoices front-end predicates
  isHidden?: (action: ActionKey, row: Row) => boolean;
};

// Hidden items are filtered OUT before descriptors are built, so divider
// placement is recomputed against the visible list.
export function buildRowActions<
  Status extends string,
  ActionKey extends string,
  Row,
>(
  config: BuildRowActionsConfig<Status, ActionKey, Row>
): (row: Row) => RowActionDescriptor<ActionKey>[] {
  return (row) => {
    const status = config.getStatus(row);
    const visibleActions = config.matrix[status].filter(
      (action) => !config.isHidden?.(action, row)
    );

    return visibleActions.map((action) => ({
      key: action,
      label: config.labels[action],
      icon: config.getIcon?.(action, row),
      destructive: config.destructiveActions?.has(action) || false,
      dividerBefore: config.dividerBeforeActions?.has(action) || false,
      disabled: config.isDisabled?.(action, row) || false,
    }));
  };
}

// Verbatim ManageEvents actionMenuPaperSx.
export const rowActionMenuPaperSx = {
  borderRadius: "8px",
  minWidth: 230,
  py: 0.5,
};

export const rowActionDestructiveItemSx = {
  color: "#DC2626",
};

const toSxArray = (sx: SxProps<Theme> | undefined) =>
  sx === undefined ? [] : Array.isArray(sx) ? sx : [sx];

export type RowActionMenuProps<ActionKey extends string = string> = {
  actions: ReadonlyArray<RowActionDescriptor<ActionKey>>;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  // the component calls onClose() first, then onAction (all four manage
  // pages close-then-act today)
  onAction: (key: ActionKey) => void;
  paperSx?: SxProps<Theme>;
  itemSx?: SxProps<Theme>;
  destructiveItemSx?: SxProps<Theme>;
};

// Children are a FLAT array of MenuItem/Divider elements — no fragments or
// wrapper divs, or MUI Menu keyboard navigation breaks.
export function RowActionMenu<ActionKey extends string = string>({
  actions,
  anchorEl,
  open,
  onClose,
  onAction,
  paperSx = rowActionMenuPaperSx,
  itemSx,
  destructiveItemSx = rowActionDestructiveItemSx,
}: RowActionMenuProps<ActionKey>) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{ sx: paperSx }}
    >
      {actions.flatMap((action, index) => {
        const menuItem = (
          <MenuItem
            key={action.key}
            disabled={action.disabled}
            onClick={() => {
              onClose();
              onAction(action.key);
            }}
            sx={
              action.destructive
                ? ([
                    ...toSxArray(itemSx),
                    ...toSxArray(destructiveItemSx),
                  ] as SxProps<Theme>)
                : itemSx
            }
          >
            {action.icon}
            {action.label}
          </MenuItem>
        );

        return action.dividerBefore && index > 0
          ? [<Divider key={`${action.key}-divider`} />, menuItem]
          : [menuItem];
      })}
    </Menu>
  );
}

// Convenience "Actions ⌄" trigger; every Phase B page overrides sx to its
// exact existing style.
export type RowActionsTriggerProps = {
  onOpen: (anchorEl: HTMLElement) => void;
  label?: string;
  sx?: SxProps<Theme>;
  endIcon?: ReactNode;
};

export const RowActionsTrigger = ({
  onOpen,
  label = "Actions",
  sx,
  endIcon = <KeyboardArrowDownIcon />,
}: RowActionsTriggerProps) => (
  <Button
    type="button"
    variant="outlined"
    endIcon={endIcon}
    onClick={(event) => onOpen(event.currentTarget)}
    sx={sx}
  >
    {label}
  </Button>
);
